import React, { useEffect, useRef } from 'react';

/**
 * The "are they on the beat" wave.
 *
 * Draws the song's reference pitch line scrolling right-to-left with the singer's
 * own pitch laid over it, so early/late/flat/sharp is visible at a glance rather
 * than being a number nobody reads mid-song.
 *
 * Canvas, not SVG or DOM: this repaints ~30 times a second on a phone that is also
 * holding a WebRTC mesh open, and a few hundred DOM nodes per frame is exactly the
 * kind of thing that makes a room drop audio.
 *
 * The reference line is always drawn. When the singer is not making a sound there is
 * simply no overlay — a flat line at zero would read as "singing badly" when the
 * truth is "not singing".
 */

/** Seconds of song visible across the canvas. */
const WINDOW_SEC = 6;
/** Semitones shown above and below the reference line. */
const RANGE = 7;

export default function BeatWave({ contour, songTime, reading, height = 92 }) {
  const canvasRef = useRef(null);
  const trailRef = useRef([]);

  // Keep the singer's recent pitch so the overlay is a line, not a dot. Reset when
  // the song restarts, or an old take's trail bleeds into the next singer's.
  useEffect(() => {
    if (songTime < 0.2) trailRef.current = [];
  }, [songTime]);

  useEffect(() => {
    if (!reading) return;
    trailRef.current.push({ t: songTime, midi: reading.singerMidi, cents: reading.cents });
    const cutoff = songTime - WINDOW_SEC;
    while (trailRef.current.length && trailRef.current[0].t < cutoff) trailRef.current.shift();
  }, [reading, songTime]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = height;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const from = songTime - WINDOW_SEC * 0.7;
    const to = songTime + WINDOW_SEC * 0.3;
    const xFor = (t) => ((t - from) / (to - from)) * w;

    // Centre the view on where the reference currently sits, so the line stays put
    // instead of sliding off the top on a high note.
    const hop = contour ? contour.hopSec : 0.032;
    const refAt = (t) => {
      if (!contour) return null;
      const v = contour.midi[Math.round(t / hop)];
      return v ? v / 10 : null;
    };
    let centre = refAt(songTime);
    if (centre == null) {
      for (let d = 0; d < 2 && centre == null; d += 0.1) centre = refAt(songTime + d);
    }
    if (centre == null) centre = 60;
    const yFor = (midi) => h / 2 - ((midi - centre) / RANGE) * (h / 2);

    // Centre guide
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();

    // "Now" marker — the singer should be hitting whatever crosses this line.
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.moveTo(xFor(songTime), 0);
    ctx.lineTo(xFor(songTime), h);
    ctx.stroke();

    // Reference contour, drawn as separate strokes per voiced run so unvoiced gaps
    // stay gaps rather than being bridged by a misleading straight line.
    if (contour) {
      // A hairline in a large dark box does not read as a "wave". Draw a soft wide
      // pass under the line so the reference has visual weight at a glance, then
      // the crisp line on top.
      ctx.strokeStyle = 'rgba(124, 196, 255, 0.16)';
      ctx.lineWidth = 11;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let pass = 0; pass < 2; pass += 1) {
        if (pass === 1) {
          ctx.strokeStyle = 'rgba(140, 205, 255, 0.95)';
          ctx.lineWidth = 3.5;
        }
        let drawing = false;
        let prev = null;
        ctx.beginPath();
        for (let t = from; t <= to; t += hop) {
          const midi = refAt(t);
          if (midi == null) { drawing = false; prev = null; continue; }
          // Break the line on a leap bigger than the visible range. Joined up, a
          // jump between two separated phrases draws as a full-height vertical bar
          // that reads as a glitch rather than as the melody it isn't.
          if (prev != null && Math.abs(midi - prev) > RANGE) { drawing = false; }
          const x = xFor(t);
          const y = yFor(midi);
          if (!drawing) { ctx.moveTo(x, y); drawing = true; } else ctx.lineTo(x, y);
          prev = midi;
        }
        ctx.stroke();
      }
    }

    // The singer, over the top.
    const trail = trailRef.current;
    if (trail.length) {
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      let drawing = false;
      let prevMidi = null;
      ctx.beginPath();
      for (const point of trail) {
        if (point.midi == null) { drawing = false; prevMidi = null; continue; }
        // Draw the singer relative to the reference, folded to the nearest octave,
        // so someone singing an octave down still tracks the line they are matching
        // instead of vanishing off the bottom of the canvas.
        const ref = refAt(point.t);
        let midi = point.midi;
        if (ref != null) {
          while (midi - ref > 6) midi -= 12;
          while (ref - midi > 6) midi += 12;
        }
        if (prevMidi != null && Math.abs(midi - prevMidi) > RANGE) drawing = false;
        const x = xFor(point.t);
        const y = yFor(midi);
        if (!drawing) { ctx.moveTo(x, y); drawing = true; } else ctx.lineTo(x, y);
        prevMidi = midi;
      }
      const cents = reading && reading.cents;
      ctx.strokeStyle = cents == null ? 'rgba(255,255,255,0.6)'
        : Math.abs(cents) < 50 ? 'rgba(94, 226, 132, 0.98)'
          : Math.abs(cents) < 120 ? 'rgba(255, 205, 84, 0.98)'
            : 'rgba(255, 122, 122, 0.98)';
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = 8;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Where the singer is right now, so the eye has something to follow.
      const head = [...trail].reverse().find((q) => q.midi != null);
      if (head) {
        const ref = refAt(head.t);
        let midi = head.midi;
        if (ref != null) {
          while (midi - ref > 6) midi -= 12;
          while (ref - midi > 6) midi += 12;
        }
        ctx.fillStyle = ctx.strokeStyle;
        ctx.beginPath();
        ctx.arc(xFor(head.t), yFor(midi), 4.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [contour, songTime, reading, height]);

  const drift = reading && reading.drift;
  const cents = reading && reading.cents;

  return (
    <div className="gs-beatwave">
      <canvas ref={canvasRef} style={{ width: '100%', height }} />
      <div className="gs-beatwave__readout">
        <span className={`gs-beatwave__chip ${
          drift == null ? '' : Math.abs(drift) <= 60 ? 'is-good' : drift < 0 ? 'is-early' : 'is-late'}`}
        >
          {drift == null ? 'Listening…'
            : Math.abs(drift) <= 60 ? 'On the beat'
              : drift < 0 ? `Ahead ${Math.abs(drift)}ms` : `Behind ${drift}ms`}
        </span>
        <span className={`gs-beatwave__chip ${
          cents == null ? '' : Math.abs(cents) < 50 ? 'is-good' : 'is-off'}`}
        >
          {cents == null ? '—'
            : Math.abs(cents) < 50 ? 'In tune'
              : cents < 0 ? `${Math.round(-cents)}¢ flat` : `${Math.round(cents)}¢ sharp`}
        </span>
      </div>
    </div>
  );
}
